import { useEditorClass } from '@tunasong/editor'
import { Check as ResolveIcon } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Chat, Comment, Persisted } from '@tunasong/schemas'
import { IconButton } from '@tunasong/ui-lib'
import type { FC } from 'react'
import ChatFeedItem from './chat-feed-item.js'
import { useChat } from './chat.hook.js'
import { useChatToast } from './chat-toast.js'
import styles from './chat-thread.module.css'

export interface ChatThreadProps {
  /** The thread: a block's child chat */
  element: Persisted<Chat>
  className?: string
}

/**
 * A block's open comments, read-only, for an anchored group in the sidebar Chat (chat makeover M3, #2718). Replies
 * are written in the block's popup. Resolve is the author's alone, and its toast is the hosting chat's, so Undo
 * outlives the group once its last comment is resolved. A plain list: the feed's scroll-to-last would make every
 * group fight over the scroll position.
 */
export const ChatThread: FC<ChatThreadProps> = props => {
  const { element, className } = props
  const ChatEditor = useEditorClass('ChatEditor')
  const { userId } = useCurrentUser()
  const showToast = useChatToast()
  const { activeComments, resolveComment, reopenComment } = useChat({ element, pageSize: 25 })

  const handleResolve = (comment: Persisted<Comment>) => () => {
    void resolveComment(comment)
    showToast({ message: 'Comment resolved.', actionLabel: 'Undo', onAction: () => void reopenComment(comment) })
  }

  if (activeComments.length === 0) {
    return null
  }

  return (
    <ol className={className ?? styles.thread}>
      {activeComments.map(comment => (
        <li key={comment.id}>
          <ChatFeedItem
            ref={null}
            item={{
              ...comment,
              content: <ChatEditor readOnly={true} autoFocus={false} element={comment} />,
              actions:
                comment.userId === userId ? (
                  <IconButton aria-label="Resolve comment" onClick={handleResolve(comment)}>
                    <ResolveIcon />
                  </IconButton>
                ) : undefined,
            }}
          />
        </li>
      ))}
    </ol>
  )
}
